import { useCallback, useState, type MouseEvent } from 'react'
import { ImageIcon } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { isAttachmentUrl, parseAttachmentIndex } from '@highlight/common/product'
import type { MarkdownImageRenderer } from '@highlight/ui'
import { useChatImage } from '@renderer/chat/hooks/useChatImage'
import { ImageView } from '@renderer/components/image-view'

export interface StagedActionAttachment {
  fileId: string
  fileName?: string
  type?: string
}

interface StagedActionImageProps {
  fileId: string
  alt?: string
  className?: string
}

export function StagedActionImage({ fileId, alt, className }: StagedActionImageProps) {
  const { imageUrl, isImageLoading, imageError } = useChatImage(fileId)
  const [isLoaded, setIsLoaded] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)

  if (imageError) return null

  const showPlaceholder = isImageLoading || !imageUrl

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (!showPlaceholder) setViewOpen(true)
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        aria-label={alt ?? 'Open image preview'}
        disabled={showPlaceholder}
        className={twMerge(
          'bg-foreground/[3%] my-2 inline-block h-24 w-24 overflow-hidden rounded-lg border p-0 align-middle',
          !showPlaceholder && 'hover:ring-foreground/20 cursor-zoom-in transition-shadow hover:ring-1',
          className,
        )}
      >
        {showPlaceholder ? (
          <span className="flex h-full w-full animate-pulse items-center justify-center">
            <ImageIcon className="text-foreground-disabled size-3" />
          </span>
        ) : (
          <img
            src={imageUrl ?? undefined}
            alt={alt ?? 'Attachment'}
            className={twMerge('h-full w-full object-cover transition-opacity', !isLoaded && 'opacity-0')}
            onLoad={() => setIsLoaded(true)}
            loading="lazy"
          />
        )}
      </button>
      {imageUrl && <ImageView src={imageUrl} alt={alt} open={viewOpen} onOpenChange={setViewOpen} />}
    </>
  )
}

// A fresh `[]` default would rebuild the renderer every render, which remounts the markdown.
const NO_ATTACHMENTS: Array<StagedActionAttachment> = []

export function useStagedActionImageRenderer(
  attachments: Array<StagedActionAttachment> = NO_ATTACHMENTS,
): MarkdownImageRenderer {
  return useCallback<MarkdownImageRenderer>(
    function StagedActionMarkdownImage({ src, alt, title, className }) {
      if (!isAttachmentUrl(src)) return <img src={src} alt={alt} title={title} className={className} />

      const index = parseAttachmentIndex(src)
      const attachment = index === null ? undefined : attachments[index]
      // Removing an attachment while editing leaves its reference in the body. Render nothing, not a broken image.
      if (!attachment) return null
      return <StagedActionImage fileId={attachment.fileId} alt={alt ?? attachment.fileName} className={className} />
    },
    [attachments],
  )
}
